import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faShieldCheck,
  faIdCard,
} from "@fortawesome/pro-solid-svg-icons";
import { buildRouteCss } from "./routeDraw";

// Live send-off timing: the route-draw plays once, after the ~7s match
// build-up, then resets the pin to center. Active window ≥ SEND_DELAY_MS +
// SEND_MS.
const MATCH_MS = 7000;
const SEND_DELAY_MS = MATCH_MS + 200; // match + a brief hold
const SEND_MS = 4800;

/**
 * Ambient mockup of the Member Portal "Match a Creator" flow.
 * Active sequence: radius rings expand, avatar dots appear, one is highlighted,
 * the profile card slides forward — then the send-off plays: the profile card
 * fades, the matched pin lifts and draws a glowing route east off the edge, and
 * resets to center. The map and other avatars stay put.
 * Resting state: map and pin remain; pin pulses slowly, low-opacity rings keep
 * expanding, dots remain visible with gentle breathing.
 * Transform/opacity only; honors prefers-reduced-motion.
 */

const MAP_BG = "#1F1F1F";
const MAP_BLOCK = "#252525";
const MAP_STREET_MINOR = "#2D2D2D";
const MAP_STREET_MAJOR = "#3A3A3A";
const MAP_WATER = "#161616";
const MAP_LAND = "#232323";
const AVATAR = "#3F3F3F";
const CARD = "#2A2A2A";
const CARD_BORDER = "#3A3A3A";
const TEAL = "#44C2E3";
const LIME = "#C0FF00";

const CSS = `
/* Active keyframes (each ends in populated/holding state at 100%) */
@keyframes hiw-match-pin-active {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.1); }
}
/* Two ring keyframes with built-in stagger so no animation-delay is needed.
   Each ring expands across half of a shared 3.2s loop. */
@keyframes hiw-match-ring-1-active {
  0% { opacity: 0.6; transform: translate(-50%, -50%) scale(0.3); }
  50% { opacity: 0; transform: translate(-50%, -50%) scale(1.8); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.8); }
}
@keyframes hiw-match-ring-2-active {
  0%, 50% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
  50.01% { opacity: 0.6; transform: translate(-50%, -50%) scale(0.3); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.8); }
}
@keyframes hiw-match-dot-1 { 0%, 15% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); } 22%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
@keyframes hiw-match-dot-2 { 0%, 18% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); } 25%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
@keyframes hiw-match-dot-3 { 0%, 21% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); } 28%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
@keyframes hiw-match-dot-4 { 0%, 24% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); } 31%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); } }
@keyframes hiw-match-dot-pick {
  0%, 27% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); box-shadow: 0 0 0 0 transparent; }
  34% { opacity: 1; transform: translate(-50%, -50%) scale(1); box-shadow: 0 0 0 0 transparent; }
  42% { opacity: 1; transform: translate(-50%, -50%) scale(1.18); box-shadow: 0 0 0 2px ${LIME}; }
  55%, 100% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); box-shadow: 0 0 0 0 transparent; }
}
@keyframes hiw-match-card {
  0%, 55% { opacity: 0; transform: translate(-50%, calc(-50% + 6px)) scale(0.92); }
  62%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
}

/* Resting ambient keyframes (slower, lower-intensity) */
@keyframes hiw-match-pin-rest {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.06); }
}
/* Two ring rest keyframes with built-in stagger inside a shared 6s loop. */
@keyframes hiw-match-ring-1-rest {
  0% { opacity: 0.18; transform: translate(-50%, -50%) scale(0.3); }
  50% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); }
}
@keyframes hiw-match-ring-2-rest {
  0%, 50% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
  50.01% { opacity: 0.18; transform: translate(-50%, -50%) scale(0.3); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(1.5); }
}
@keyframes hiw-match-dot-breathe {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(1.05); }
}

/* Camera zoom - wide shot during search, zoom in to MT pickup, then pull back
   to wide so the profile card fits cleanly when it emerges. */
@keyframes hiw-match-camera {
  0%, 30% { transform: scale(1); transform-origin: 50% 50%; }
  42% { transform: scale(1.5); transform-origin: 55% 65%; }
  50% { transform: scale(1.5); transform-origin: 55% 65%; }
  62%, 88% { transform: scale(1); transform-origin: 50% 50%; }
  95%, 100% { transform: scale(1); transform-origin: 50% 50%; }
}

.hiw-anim { will-change: transform, opacity; }

/* Active */
.hiw-match-pin-active { animation: hiw-match-pin-active 1.6s ease-in-out infinite; }
.hiw-match-ring-1-active { animation: hiw-match-ring-1-active 3.2s ease-out infinite; }
.hiw-match-ring-2-active { animation: hiw-match-ring-2-active 3.2s ease-out infinite; }
.hiw-match-dot-1 { animation: hiw-match-dot-1 7s ease-out forwards; }
.hiw-match-dot-2 { animation: hiw-match-dot-2 7s ease-out forwards; }
.hiw-match-dot-3 { animation: hiw-match-dot-3 7s ease-out forwards; }
.hiw-match-dot-4 { animation: hiw-match-dot-4 7s ease-out forwards; }
.hiw-match-dot-pick { animation: hiw-match-dot-pick 7s ease-out forwards; }
.hiw-match-card { animation: hiw-match-card 7s ease-out forwards; }

/* Resting */
.hiw-match-pin-rest { animation: hiw-match-pin-rest 3.2s ease-in-out infinite; }
.hiw-match-ring-1-rest { animation: hiw-match-ring-1-rest 6s ease-out infinite; }
.hiw-match-ring-2-rest { animation: hiw-match-ring-2-rest 6s ease-out infinite; }
.hiw-match-dot-breathe-1 { animation: hiw-match-dot-breathe 2.6s ease-in-out infinite; }
.hiw-match-dot-breathe-2 { animation: hiw-match-dot-breathe 3.1s ease-in-out infinite; }
.hiw-match-dot-breathe-3 { animation: hiw-match-dot-breathe 2.4s ease-in-out infinite; }
.hiw-match-dot-breathe-4 { animation: hiw-match-dot-breathe 3.4s ease-in-out infinite; }

.hiw-match-camera { animation: hiw-match-camera 7s cubic-bezier(0.4, 0, 0.2, 1) forwards; will-change: transform; }

@media (prefers-reduced-motion: reduce) {
  .hiw-anim { animation: none !important; opacity: 1 !important; transform: translate(-50%, -50%) !important; }
  .hiw-match-camera { animation: none !important; transform: none !important; }
}

/* ── Live send-off: route draw (plays once, after the match) ────────────── */
${buildRouteCss(SEND_MS, SEND_DELAY_MS)}
`;

const DOTS = [
  { breatheCls: "hiw-match-dot-breathe-1", activeCls: "hiw-match-dot-1", top: "28%", left: "22%", initials: "AB" },
  { breatheCls: "hiw-match-dot-breathe-2", activeCls: "hiw-match-dot-2", top: "72%", left: "28%", initials: "CD" },
  { breatheCls: "hiw-match-dot-breathe-3", activeCls: "hiw-match-dot-3", top: "22%", left: "76%", initials: "EF" },
  { breatheCls: "hiw-match-dot-breathe-4", activeCls: "hiw-match-dot-4", top: "76%", left: "74%", initials: "GH" },
];

interface Props {
  isActive: boolean;
}

const MatchAnimation = ({ isActive }: Props) => {
  // When resting, the map, rings and dots keep their calm breathing and stay
  // put, sharing the resting variants.
  const restMode = !isActive;

  return (
    <div
      className="hiw-match-root relative aspect-[4/3] overflow-hidden"
      style={{ background: MAP_BG, contain: "paint", containerType: "inline-size" }}
    >
      <style>{CSS}</style>

      <div className={`absolute inset-0 ${isActive ? "hiw-anim hiw-match-camera" : ""}`}>

      {/* Stylized dark-mode city map (static SVG, no animation, no text) */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 400 300"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        {/* Water - coastline curving into bottom-right corner */}
        <path
          d="M 410 192 Q 388 198 368 214 Q 348 232 338 262 Q 328 292 322 310 L 410 310 Z"
          fill={MAP_WATER}
        />

        {/* Park / campus - irregular shape in upper-right corner */}
        <path
          d="M 312 -10 L 410 -10 L 410 82 Q 392 86 376 80 Q 356 70 344 54 Q 326 36 320 18 L 316 0 Z"
          fill={MAP_LAND}
        />

        {/* Building blocks - clustered in dense zones, scattered in mid-area, varied sizes */}
        <g fill={MAP_BLOCK}>
          {/* Upper-left dense cluster */}
          <rect x="15" y="15" width="18" height="22" rx="0.5" />
          <rect x="38" y="12" width="20" height="18" rx="0.5" />
          <rect x="63" y="18" width="16" height="24" rx="0.5" />
          <rect x="84" y="15" width="22" height="20" rx="0.5" />
          <rect x="111" y="14" width="20" height="22" rx="0.5" />
          <rect x="12" y="44" width="22" height="16" rx="0.5" />
          <rect x="40" y="42" width="18" height="20" rx="0.5" />
          <rect x="64" y="48" width="20" height="22" rx="0.5" />
          <rect x="90" y="42" width="16" height="26" rx="0.5" />
          <rect x="112" y="46" width="24" height="22" rx="0.5" />

          {/* Mid-area scatter */}
          <rect x="158" y="100" width="26" height="18" rx="0.5" />
          <rect x="190" y="98" width="18" height="22" rx="0.5" />
          <rect x="215" y="105" width="22" height="20" rx="0.5" />
          <rect x="245" y="100" width="28" height="22" rx="0.5" />
          <rect x="160" y="170" width="24" height="30" rx="0.5" />
          <rect x="192" y="172" width="20" height="28" rx="0.5" />
          <rect x="220" y="170" width="18" height="32" rx="0.5" />
          <rect x="245" y="172" width="26" height="28" rx="0.5" />

          {/* Lower-left dense cluster */}
          <rect x="15" y="240" width="18" height="20" rx="0.5" />
          <rect x="38" y="244" width="20" height="16" rx="0.5" />
          <rect x="62" y="240" width="16" height="22" rx="0.5" />
          <rect x="84" y="245" width="22" height="18" rx="0.5" />
          <rect x="112" y="240" width="24" height="20" rx="0.5" />
          <rect x="12" y="270" width="20" height="24" rx="0.5" />
          <rect x="38" y="272" width="16" height="20" rx="0.5" />
          <rect x="60" y="270" width="22" height="22" rx="0.5" />
          <rect x="88" y="272" width="18" height="22" rx="0.5" />
          <rect x="114" y="270" width="22" height="24" rx="0.5" />

          {/* Lower-right scatter, away from water */}
          <rect x="158" y="248" width="24" height="18" rx="0.5" />
          <rect x="190" y="248" width="18" height="22" rx="0.5" />
          <rect x="220" y="248" width="26" height="20" rx="0.5" />
          <rect x="254" y="248" width="22" height="22" rx="0.5" />
        </g>

        {/* Minor / tertiary streets - dense residential mesh in upper-left and lower-left, plus quiet mid connectors */}
        <g stroke={MAP_STREET_MINOR} strokeWidth="0.6" fill="none" strokeLinecap="round">
          {/* Upper-left mesh */}
          <line x1="0" y1="38" x2="145" y2="38" />
          <line x1="35" y1="0" x2="35" y2="80" />
          <line x1="60" y1="0" x2="60" y2="80" />
          <line x1="86" y1="0" x2="86" y2="80" />
          <line x1="111" y1="0" x2="111" y2="80" />

          {/* Lower-left mesh */}
          <line x1="0" y1="262" x2="145" y2="262" />
          <line x1="0" y1="288" x2="145" y2="288" />
          <line x1="35" y1="235" x2="35" y2="310" />
          <line x1="58" y1="235" x2="58" y2="310" />
          <line x1="82" y1="235" x2="82" y2="310" />
          <line x1="108" y1="235" x2="108" y2="310" />

          {/* Mid connectors */}
          <line x1="180" y1="125" x2="280" y2="128" />
          <line x1="190" y1="200" x2="270" y2="202" />

          {/* Diagonal alley */}
          <path d="M 250 240 L 295 290" />

          {/* Curved campus drive inside park */}
          <path d="M 330 0 Q 350 25 380 40 Q 400 50 410 55" />
        </g>

        {/* Secondary streets - city's structural bones, MAJOR tone at thinner weight */}
        <g stroke={MAP_STREET_MAJOR} strokeWidth="1" fill="none" strokeLinecap="round">
          <line x1="0" y1="80" x2="160" y2="80" />
          <line x1="180" y1="82" x2="310" y2="80" />
          <line x1="0" y1="150" x2="155" y2="150" />
          <line x1="195" y1="152" x2="410" y2="155" />
          <line x1="155" y1="0" x2="160" y2="100" />
          <line x1="158" y1="118" x2="162" y2="220" />
          <line x1="225" y1="125" x2="228" y2="248" />
          <line x1="275" y1="125" x2="278" y2="248" />
          <line x1="0" y1="260" x2="155" y2="262" />
          <line x1="200" y1="270" x2="310" y2="272" />
          <line x1="60" y1="80" x2="62" y2="150" />
          <line x1="85" y1="150" x2="87" y2="222" />
        </g>

        {/* Major arterials - 3 thick paths, one strongly curved */}
        <g stroke={MAP_STREET_MAJOR} strokeWidth="1.8" fill="none" strokeLinecap="round">
          {/* Curved diagonal sweeping across upper third */}
          <path d="M -10 75 Q 90 60 180 85 Q 270 110 365 95 L 410 88" />
          {/* Gently curving vertical arterial */}
          <path d="M 140 -10 Q 152 80 168 175 Q 178 250 158 310" />
          {/* Horizontal arterial across lower third */}
          <path d="M -10 222 Q 90 218 175 224 L 410 230" />
        </g>
      </svg>

      {/* Radius rings - swap between active and resting variants */}
      <span
        className={`hiw-anim absolute top-1/2 left-1/2 rounded-full pointer-events-none ${restMode ? "hiw-match-ring-1-rest" : "hiw-match-ring-1-active"}`}
        style={{
          width: 180,
          height: 180,
          border: `2px solid ${TEAL}`,
          transform: "translate(-50%, -50%)",
        }}
      />
      <span
        className={`hiw-anim absolute top-1/2 left-1/2 rounded-full pointer-events-none ${restMode ? "hiw-match-ring-2-rest" : "hiw-match-ring-2-active"}`}
        style={{
          width: 180,
          height: 180,
          border: `2px solid ${TEAL}`,
          transform: "translate(-50%, -50%)",
        }}
      />

      {/* Center pin — pulses during the match, then hands off to the send-off
          pin (which lifts and draws the route); rests with a slow pulse. */}
      <span
        className={`hiw-anim absolute top-1/2 left-1/2 w-9 h-9 rounded-full flex items-center justify-center ${isActive ? "hiw-match-pin-send" : "hiw-match-pin-rest"}`}
        style={{
          background: TEAL,
          boxShadow: `0 0 0 6px rgba(68,194,227,0.18)`,
          transform: "translate(-50%, -50%)",
        }}
      >
        <FontAwesomeIcon icon={faLocationDot} className="w-4 h-4" style={{ color: "#0E0E0E" }} />
      </span>

      {/* Creator dots */}
      {DOTS.map((dot) => (
        <span
          key={dot.initials}
          className={`hiw-anim absolute w-9 h-9 rounded-full flex items-center justify-center text-[10px] font-black text-white ${restMode ? dot.breatheCls : dot.activeCls}`}
          style={{
            top: dot.top,
            left: dot.left,
            background: AVATAR,
            transform: "translate(-50%, -50%)",
          }}
        >
          {dot.initials}
        </span>
      ))}

      {/* Picked dot - only rendered during the active match flow */}
      {isActive && (
        <span
          className="hiw-anim hiw-match-dot-pick absolute w-10 h-10 rounded-full flex items-center justify-center text-[10px] font-black text-white"
          style={{
            top: "70%",
            left: "55%",
            background: AVATAR,
          }}
        >
          MT
        </span>
      )}

      {/* Profile card - only rendered during the active match flow */}
      {isActive && (
        <div
          className="hiw-anim hiw-match-card-send absolute top-1/2 left-1/2 w-[80%] rounded-2xl p-4"
          style={{
            background: CARD,
            border: `1px solid ${CARD_BORDER}`,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.45)",
          }}
        >
          <div className="flex items-center gap-3">
            <span
              className="w-11 h-11 rounded-full flex items-center justify-center text-[13px] font-black text-white"
              style={{ background: AVATAR }}
            >
              MT
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-black text-white tracking-tight leading-tight">
                Marcus T.
              </div>
              <div className="text-[10px] font-medium text-zinc-400 mt-0.5">12.4 mi away</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {["Lacrosse", "Highlights"].map((tag) => (
              <span
                key={tag}
                className="text-[10px] font-bold text-black rounded-full px-2.5 py-0.5"
                style={{ background: TEAL }}
              >
                {tag}
              </span>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span
              className="flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-0.5"
              style={{ background: "rgba(68,194,227,0.16)", color: TEAL }}
            >
              <FontAwesomeIcon icon={faShieldCheck} className="w-3 h-3" />
              Verified
            </span>
            <span
              className="flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-0.5"
              style={{ background: "rgba(68,194,227,0.16)", color: TEAL }}
            >
              <FontAwesomeIcon icon={faIdCard} className="w-3 h-3" />
              Background checked
            </span>
          </div>
        </div>
      )}

      {/* Live send-off: the matched pin lifts, draws a glowing route east and
          exits, then resets to center (active path only). */}
      {isActive && (
        <>
          <div
            className="hiw-match-send-el hiw-match-route absolute top-1/2 left-1/2 z-20 pointer-events-none"
            style={{
              width: "80cqw",
              height: 3,
              borderRadius: 9999,
              transformOrigin: "left center",
              transform: "translateY(-50%) scaleX(0)",
              background: `linear-gradient(to right, ${TEAL}, rgba(68,194,227,0.35))`,
              boxShadow: "0 0 12px rgba(68,194,227,0.85)",
            }}
          />
          <div className="hiw-match-send-el hiw-match-send-pin absolute inset-0 z-20">
            <span
              className="hiw-match-send-el hiw-match-send-glow absolute top-1/2 left-1/2 w-9 h-9 rounded-full pointer-events-none"
              style={{
                background: "radial-gradient(circle, rgba(68,194,227,0.55) 0%, rgba(68,194,227,0) 70%)",
                transform: "translate(-50%, -50%)",
              }}
            />
            <span
              className="hiw-match-send-el hiw-match-send-face absolute top-1/2 left-1/2 w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: TEAL, boxShadow: `0 0 0 6px rgba(68,194,227,0.18)`, transform: "translate(-50%, -50%)" }}
            >
              <FontAwesomeIcon icon={faLocationDot} className="w-4 h-4" style={{ color: "#0E0E0E" }} />
            </span>
          </div>
        </>
      )}

      </div>
    </div>
  );
};

export default MatchAnimation;
